/**
 * useTokenBalance Hook
 *
 * Consumes token balance from TokenBalanceContext.
 * All instances share the same state - only ONE API call is made.
 *
 * Re-exports TokenBalanceInfo type for backward compatibility.
 */

import { useContext } from 'react';
import { TokenBalanceContext, type TokenBalanceContextType } from '../contexts/TokenBalanceContext';

// Re-export type for consumers that import it from here
export type { TokenBalanceInfo } from '../contexts/TokenBalanceContext';

interface UseTokenBalanceReturn {
  /** Total balance (expirable + permanent); null when not known (see TokenBalanceContext). */
  balance: number | null;
  /** Full balance breakdown */
  fullBalance: TokenBalanceContextType['fullBalance'];
  /** Loading state */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refresh balance from server */
  refresh: () => Promise<void>;
}

/**
 * Hook to access user's token balance from centralized context.
 *
 * @returns Token balance state with full breakdown and refresh function
 */
export function useTokenBalance(): UseTokenBalanceReturn {
  const context = useContext(TokenBalanceContext);

  if (!context) {
    throw new Error('useTokenBalance must be used within a TokenBalanceProvider');
  }

  return context;
}

/**
 * Notify all useTokenBalance consumers to refresh.
 * Call this after any operation that deducts tokens.
 */
export function notifyTokenBalanceChanged(): void {
  window.dispatchEvent(new CustomEvent('token-balance-changed'));
}
